/**
 * H2H summary (matches design): big team-coloured win tallies, a proportional results bar,
 * and a goals-scored row. Win/bar colours come from each team's kit; goals are gold.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { distinguishTeamColors } from '../../../helpers/distinguishTeamColors.helper';
import type { MatchHubTeamSide } from '../../../src/match/types/matchHub.types';
import { getMatchHubH2HSummaryHeaderStyles } from './MatchHubH2HSummaryHeader.styles';

export interface MatchHubH2HSummaryHeaderProps {
  homeWins: number;
  draws: number;
  awayWins: number;
  goalsHome: number;
  goalsAway: number;
  home: MatchHubTeamSide;
  away: MatchHubTeamSide;
}

export const MatchHubH2HSummaryHeader: React.FC<MatchHubH2HSummaryHeaderProps> = ({
  homeWins,
  draws,
  awayWins,
  goalsHome,
  goalsAway,
  home,
  away,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubH2HSummaryHeaderStyles(theme), [theme]);

  const { home: homeColor, away: awayColor } = useMemo(
    () => distinguishTeamColors(home.kit.primary, away.kit.primary),
    [home.kit.primary, away.kit.primary],
  );
  const total = homeWins + draws + awayWins;

  return (
    <View>
      <View style={styles.tallyRow}>
        <View>
          <Text style={[styles.tallyNumber, { color: homeColor }]}>{homeWins}</Text>
          <Text style={styles.tallyLabel}>{home.shortName} wins</Text>
        </View>
        <View style={styles.tallyColCenter}>
          <Text style={[styles.tallyNumber, { color: theme.gray[500] }]}>{draws}</Text>
          <Text style={styles.tallyLabel}>Draws</Text>
        </View>
        <View style={styles.tallyColRight}>
          <Text style={[styles.tallyNumber, { color: awayColor }]}>{awayWins}</Text>
          <Text style={styles.tallyLabel}>{away.shortName} wins</Text>
        </View>
      </View>

      <View style={styles.barTrack}>
        {total > 0 ? (
          <>
            {homeWins > 0 && <View style={[styles.barSeg, { flex: homeWins, backgroundColor: homeColor }]} />}
            {draws > 0 && <View style={[styles.barSeg, styles.barSegDraws, { flex: draws }]} />}
            {awayWins > 0 && <View style={[styles.barSeg, { flex: awayWins, backgroundColor: awayColor }]} />}
          </>
        ) : null}
      </View>

      <View style={styles.goalsRow}>
        <View>
          <Text style={styles.goalsNumber}>{goalsHome}</Text>
          <Text style={styles.tallyLabel}>Goals scored</Text>
        </View>
        <View style={styles.tallyColRight}>
          <Text style={styles.goalsNumber}>{goalsAway}</Text>
          <Text style={styles.tallyLabel}>Goals scored</Text>
        </View>
      </View>
    </View>
  );
};
